// Copyright (c) Docugami, Inc. All rights reserved.

import { MutableRefObject } from "react";
import { ComponentId } from "sdv/engine/Component";
import { EntityManager, IEntityManager } from "sdv/engine/Entity";
import { EntityQuery } from "sdv/engine/EntityQuery";
import { IExternalCallbacks } from "sdv/ExternalCallbacks";
import { SdvInterface } from "sdv/SdvInterface";
import { ErrorLimiter, IErrorLimiter } from "utils/ErrorLimiter";
import { IInput, IInputSource, InputController } from "../input/InputController";
import { IResources, IResourcesView, Resources } from "../Resources";
import { Clock } from "./Clock";
import { ViewComponent } from "sdv/components/ViewComponent";

export interface IEngineConfig {
    // The rate at which the engine updates. Undefined will result
    // in an update rate equivalent to that of the window animation frame.
    fps?: number;

    // The rate at which we render to the canvas. This cannot be more
    // frequent than we update the engine.  Undefiend will result in an
    // update rate equivalent to that of the window animation frame.
    renderFps?: number;
}

const defaultConfig: IEngineConfig = {};

export type UpdateCommand = (entityManager: IEntityManager, resources: IResources) => void;

export type Queries = {
    [name: string]: {
        types: ComponentId[],
        query?: EntityQuery
    }
}

export interface ISystemInput {
    dt: number,
    resources: IResources,
    input: IInput,
    entities: IEntityManager,
    callbacks: IExternalCallbacks,
    errorLimiter: IErrorLimiter
}

export interface IRendererInput {
    dt: number,
    resources: IResources,
    entities: IEntityManager
}

export enum RenderLayerClearBehavior {
    Always,
    OnDraw
}

export interface IRenderLayer {
    renderers: IRenderer[];
    clearBehavior?: RenderLayerClearBehavior;
    canvas?: MutableRefObject<HTMLCanvasElement | undefined>;
    disableAlpha?: boolean;
}

export interface ISystem extends IQueryEntities {
    initialize?: (sdvInterface: SdvInterface) => void;
    update: () => void;
    updateEnabled: boolean;

    dt?: number;
    resources?: IResources,
    input?: IInput,
    entities?: IEntityManager,
    callbacks?: IExternalCallbacks,
    sdvInterface?: SdvInterface,
    errorLimiter?: IErrorLimiter;
}

export interface IQueryEntities {
    queries?: Queries;
}

export interface IRenderer {
    render: (data: IRendererInput) => IDrawCommand[];
    renderEnabled: boolean;
    queries?: Queries;
}

export interface IDrawCommand {
    (context: CanvasRenderingContext2D): void;
}

/**
 * The core engine for the SDV. Responsible for input, systems, and
 * rendering the scene.
 */
export class Engine {
    private _isRunning: boolean = false;
    private readonly _inputController: InputController;
    private readonly _clock: Clock;
    private readonly _systems: ISystem[];
    private readonly _resources: Resources;
    private readonly _entityManager: EntityManager;
    private readonly _layers: IRenderLayer[];
    private _preUpdateCommands: UpdateCommand[] = [];
    private _postUpdateCommands: UpdateCommand[] = [];
    private readonly _inputSources: IInputSource[];
    private readonly _externalCallbacks: IExternalCallbacks;
    private readonly _sdvInterface: SdvInterface;
    private readonly _config: IEngineConfig;
    private _accumulator: number = 0;
    private _renderAccumulator: number = 0;
    private _errorLimiter: ErrorLimiter = new ErrorLimiter();

    constructor(
        systems: ISystem[],
        inputSources: IInputSource[],
        layers: IRenderLayer[],
        callbacks: IExternalCallbacks,
        config: Partial<IEngineConfig> = {}) {

        this._systems = systems;
        this._inputSources = inputSources;
        this._layers = layers;
        this._externalCallbacks = callbacks;

        this._resources = new Resources();
        this._entityManager = new EntityManager();
        this._inputController = new InputController();
        this._clock = new Clock();

        this._config = {
            ...defaultConfig,
            ...config
        };

        this._sdvInterface = {
            setResource: (key, value) => this.queuePreUpdateCommand((_, resources) => resources.set(key, value)),
            entities: this._entityManager
        }
    }

    get inputController(): InputController { return this._inputController; }
    get resourceView(): IResourcesView { return this._resources; }
    get sdvInterface(): SdvInterface { return this._sdvInterface; }

    private get fps(): number { return this._config.fps ?? 30.0 }

    queuePreUpdateCommand(command: UpdateCommand) {
        this._preUpdateCommands.push(command);
    }

    queuePostUpdateCommand(command: UpdateCommand) {
        this._postUpdateCommands.push(command);
    }

    run() {
        if (this._isRunning) {
            return;
        }

        this._isRunning = true;

        this._clock.start();
        this._inputSources.forEach(x => x.start(this._inputController));

        this._systems.forEach(s => {
            if (!!s.initialize) {
                s.initialize(this._sdvInterface);
            }
        })

        this.update(this._clock.currentTime);
    }

    stop() {
        if (!this._isRunning) {
            return;
        }

        this._inputSources.forEach(x => x.stop());
        this._accumulator = 0;
        this._renderAccumulator = 0;
        this._isRunning = false;
        this._errorLimiter.flush();
    }

    private update(currentTime: number) {
        if (!this._isRunning) {
            return;
        }

        this._clock.tick(currentTime);

        let deltaTime = this._clock.deltaTime / 1000.0;
        if (deltaTime > .5) {
            // Likely debugger attached or paused so we can just reset our dt for a frame
            deltaTime = 1 / this.fps;
        }

        let frameDt: number;
        if (!!this._config.fps) {
            // We have a target FPS, use our accumulator to maintain it
            this._accumulator += deltaTime;
            frameDt = 1 / this.fps;
        }
        else {
            this._accumulator = deltaTime;
            frameDt = deltaTime;
        }

        const shouldRender = this.updateRenderAccumulator(deltaTime);
        while (frameDt > 0 && this._accumulator >= frameDt) {
            this._accumulator -= frameDt;

            const render: boolean = this._accumulator < frameDt && shouldRender;

            try {
                this.performUpdate(frameDt, render);
            }
            catch (error) {
                this._errorLimiter.handle(error);
            }
        }

        // Request our next frame
        window.requestAnimationFrame(this.update.bind(this))
    }

    private performUpdate(dt: number, render: boolean) {
        this._preUpdateCommands.forEach(c => c(this._entityManager, this._resources));
        this._preUpdateCommands = [];

        this._resources.preUpdate();

        // Input sources update the controller, so update them first and then snapshot the input
        this._inputSources.forEach(x => x.update());
        const input = this._inputController.input;

        // Collect our input for systems
        const systemInput: ISystemInput = {
            dt: dt,
            resources: this._resources,
            input: input,
            entities: this._entityManager,
            callbacks: this._externalCallbacks,
            errorLimiter: this._errorLimiter
        };

        // Update the systems
        this._systems.forEach(x => {
            Object.assign(x, systemInput);
            if (x.updateEnabled) {
                this.fillQueries(x);
                x.update();
            }
        });

        this._entityManager.postUpdate();

        if (render) {
            // Render
            this.render(dt);
        }

        this._postUpdateCommands.forEach(c => c(this._entityManager, this._resources));
        this._postUpdateCommands = [];
        this._resources.postUpdate();
    }

    private updateRenderAccumulator(deltaTime: number): boolean {
        if (!this._config.renderFps) {
            return true;
        }

        const renderDt = (1 / this._config.renderFps);
        this._renderAccumulator += deltaTime;
        const shouldRender = this._renderAccumulator > renderDt;

        this._renderAccumulator %= renderDt;
        return shouldRender;
    }

    private fillQueries(target: IQueryEntities) {
        if (!!target.queries) {
            for (const key in target.queries) {
                const val = target.queries[key];
                const query = this._entityManager.query(val.types);
                val.query = query;
            }
        }
    }

    private render(dt: number) {
        const rendererInput: IRendererInput = {
            dt: dt,
            resources: this._resources,
            entities: this._entityManager
        };


        for (const layer of this._layers) {
            this.renderLayer(layer, rendererInput);
        }
    }

    private renderLayer(layer: IRenderLayer, input: IRendererInput) {
        const viewComponent = this._entityManager.query([ComponentId.View]).single()?.getComponent<ViewComponent>(ComponentId.View);
        if (!viewComponent) {
            return;
        }

        const context = layer.canvas?.current?.getContext('2d', !!layer.disableAlpha ? { alpha: false } : undefined);
        if (!!context || layer.canvas === undefined) {
            const drawCommands = layer.renderers.flatMap(r => {
                if (r.renderEnabled) {
                    this.fillQueries(r);
                    return r.render(input);
                }

                return [];
            });

            if (!!context && !!layer.canvas?.current && layer?.clearBehavior !== undefined) {
                switch (layer.clearBehavior) {
                    case RenderLayerClearBehavior.Always: {
                        this.prepareFrame(context, layer.canvas.current.width, layer.canvas.current.height);
                        break;
                    }

                    case RenderLayerClearBehavior.OnDraw: {
                        if (!!drawCommands.length) {
                            this.prepareFrame(context, layer.canvas.current.width, layer.canvas.current.height);
                        }
                        break;
                    }
                }
            }

            if (!!context) {
                drawCommands.forEach(r => {
                    context.resetTransform();
                    context.scale(Math.max(window.devicePixelRatio, 2), Math.max(window.devicePixelRatio, 2))
                    context.translate(-viewComponent.viewport.worldPosition!.x, -viewComponent.viewport.worldPosition!.y);
                    r(context)
                    context.resetTransform();
                });
            }
        }
    }

    private prepareFrame(context: CanvasRenderingContext2D, width: number, height: number): void {
        context.globalAlpha = 1;
        context.globalCompositeOperation = 'source-over';
        context.clearRect(0, 0, width, height);
    }
}
